import { Skeleton } from '@avanta/shared/components/ui/Skeleton';
import { cn } from '@avanta/shared/utils/cn';

interface SessionSummarySkeletonProps {
  sections: {
    dateTime: boolean;
    patient: boolean;
    service: boolean;
    psychologist: boolean;
  };
}

const InfoRowSkeleton = ({
  labelWidth,
  descriptionWidth,
}: {
  labelWidth: string;
  descriptionWidth?: string;
}): React.ReactElement => (
  <div className={cn('flex gap-2', descriptionWidth ? 'items-start' : 'items-center')}>
    <Skeleton className="size-4 shrink-0" />
    <div className="flex min-w-0 flex-col gap-1">
      <Skeleton className={cn('h-4', labelWidth)} />
      {descriptionWidth ? <Skeleton className={cn('h-3', descriptionWidth)} /> : null}
    </div>
  </div>
);

const SectionTitleSkeleton = (): React.ReactElement => <Skeleton className="h-4 w-32" />;

export const SessionSummarySkeleton = ({
  sections,
}: SessionSummarySkeletonProps): React.ReactElement => (
  <div
    className="flex w-74 shrink-0 flex-col gap-4 rounded-xl border border-teal-300 p-6"
    aria-busy="true"
  >
    <div className="contents" aria-hidden="true">
      {sections.dateTime ? (
        <>
          <div className="flex flex-col gap-2">
            <InfoRowSkeleton labelWidth="w-40" />
            <InfoRowSkeleton labelWidth="w-16" />
          </div>
          <div className="h-px w-full bg-teal-300" />
        </>
      ) : null}
      {sections.patient ? (
        <div className="mb-2 flex flex-col gap-4">
          <SectionTitleSkeleton />
          <div className="flex flex-col gap-2">
            <InfoRowSkeleton labelWidth="w-40" />
            <InfoRowSkeleton labelWidth="w-28" />
          </div>
        </div>
      ) : null}
      {sections.service ? (
        <div className="flex flex-col gap-4">
          <SectionTitleSkeleton />
          <div className="flex flex-col gap-2">
            <InfoRowSkeleton labelWidth="w-44" />
            <InfoRowSkeleton labelWidth="w-36" />
            <InfoRowSkeleton labelWidth="w-24" />
            <InfoRowSkeleton labelWidth="w-44" descriptionWidth="w-36" />
          </div>
        </div>
      ) : null}
      {sections.psychologist ? (
        <>
          <div className="h-px w-full bg-teal-300" />
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <Skeleton className="size-14 shrink-0 rounded-full" />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-4 w-36" />
                <Skeleton className="mt-3 h-3 w-full" />
                <Skeleton className="h-3 w-4/5" />
              </div>
            </div>
            <div className="flex gap-2">
              <Skeleton className="size-4 shrink-0" />
              <div className="flex flex-1 flex-col gap-1">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  </div>
);
